// main.ts — Portal: browser UI for WebUSB pull + QA + upload (EGO-57/64).
// Connection lifecycle + session/collection listing + pull with the
// verify-then-delete semantics from src/pull.ts. The `__e2e` hooks at the
// bottom let an automated driver run the whole path against OPFS (no native
// dialogs), which is how the overnight E2E exercises it.
import { Adb, AdbDaemonTransport } from "@yume-chan/adb";
import { AdbDaemonWebUsbDeviceManager } from "@yume-chan/adb-daemon-webusb";
import AdbWebCredentialStore from "@yume-chan/adb-credential-web";
import { listRemote, pullSession, pullCollection, rmRemote, recRoot, colRoot, type PullProgress } from "./pull.ts";
import { handleWriter } from "./fsdir.ts";
import { initAuth, supabase, getProfile } from "./auth.ts";
import { renderTasks } from "./tasks.ts";
import { latestRelease, installedBuild, downloadApk, installApk, type ReleaseInfo } from "./apk.ts";
import { uploadSession, uploadedStatus, type UploadedRow, type UploadProgress } from "./upload.ts";
import { transcribeInto } from "./transcribe.ts";
import { proxyInto } from "./proxy.ts";
import { maskBurnInto } from "./maskburn.ts";
import { openVerify, closeVerify, isVerifyOpen } from "./verify.ts";
import { renderUploads } from "./uploads.ts";
import { SESSION_ID_RE } from "./pull.ts";
import { startWizard } from "./wizard.ts";
import { breadcrumb, reportError, errmonUser } from "./errmon.ts";

const PKG = "com.Fluid.XRBase";

// Error monitoring: every auth change re-tags Bugsnag events with the user.
supabase.auth.onAuthStateChange((_event, session) => {
  errmonUser(session?.user?.id ?? null, session?.user?.email);
});

const $ = (id: string) => document.getElementById(id)!;
const logEl = $("log") as HTMLPreElement;
const rowsEl = $("rows");
const btnConnect = $("btnConnect") as HTMLButtonElement;
const btnDisconnect = $("btnDisconnect") as HTMLButtonElement;
const btnLaunch = $("btnLaunch") as HTMLButtonElement;
const btnFolder = $("btnFolder") as HTMLButtonElement;
const btnRefresh = $("btnRefresh") as HTMLButtonElement;
const btnPullAll = $("btnPullAll") as HTMLButtonElement;
const optDelete = $("optDelete") as HTMLInputElement;
const outDirEl = $("outDir");
const progressEl = $("progress");
const progFile = $("progFile");
const progBar = $("progBar") as HTMLElement;
const progPhase = $("progPhase");
const progCtx = $("progCtx");
const progTrack = $("progTrack");

// One painter for the whole sync flow: a named phase, a detail line, and a bar
// that's determinate when there's a byte total and an indeterminate sweep when
// there isn't (transcribe / checksum / commit) — so no phase ever reads as
// frozen. syncCtx labels "session k/n" during a multi-session Sync now.
let syncCtx = { idx: 0, total: 0 };
function paint(phase: string, detail: string, frac: number | null) {
  progressEl.hidden = false;
  progPhase.textContent = phase;
  progCtx.textContent = syncCtx.total > 1 ? `session ${syncCtx.idx}/${syncCtx.total}` : "";
  progFile.textContent = detail;
  if (frac == null) { progTrack.classList.add("indet"); progBar.style.width = ""; }
  else { progTrack.classList.remove("indet"); progBar.style.width = Math.round(frac * 100) + "%"; }
}
function hideProgress() { progressEl.hidden = true; }

function log(msg: string) {
  logEl.textContent += msg + "\n";
  logEl.scrollTop = logEl.scrollHeight;
  console.log("[portal]", msg);
  breadcrumb(msg);   // an error event carries the whole session narrative
}
const countsLine = $("countsLine");
function setCounts(msg: string) { countsLine.textContent = msg; }
const fmtMB = (b: number) => (b / 1048576).toFixed(1) + " MB";
const fmtSize = (b: number) => b >= 1073741824 ? (b / 1073741824).toFixed(1) + " GB" : fmtMB(b);

// EGO-65: best-effort disk-space preflight before a headset→PC pull. The web
// platform has no API for the free space of an arbitrary picked directory;
// navigator.storage.estimate() reports the origin's quota headroom, which
// Chromium derives from the disk backing the browser profile — authoritative
// for OPFS staging, a decent proxy for a picked folder on the same volume,
// and null where unsupported. Heuristic, so the user can override; without it
// a too-big pull just dies mid-stream with an opaque write error.
async function spaceHeadroom(): Promise<number | null> {
  try {
    const { usage, quota } = await navigator.storage.estimate();
    return quota != null ? quota - (usage ?? 0) : null;
  } catch {
    return null;
  }
}
async function confirmSpace(needed: number, what: string): Promise<boolean> {
  const free = await spaceHeadroom();
  if (free == null || free >= needed * 1.1) return true;   // 10% margin for fs overhead
  log(`⚠ low disk space: ${what} needs ${fmtSize(needed)} but the browser reports only ~${fmtSize(Math.max(0, free))} available`);
  return confirm(`Low disk space.\n\nPulling ${what} needs ${fmtSize(needed)}, but only ~${fmtSize(Math.max(0, free))} appears to be available on this PC.\n\nPull anyway?`);
}

// The same estimate, surfaced passively in the UI next to the folder line.
const diskFreeEl = $("diskFree");
async function updateDiskFree() {
  const free = await spaceHeadroom();
  diskFreeEl.textContent = free != null ? ` · ~${fmtSize(Math.max(0, free))} free for pulls (est.)` : "";
}
void updateDiskFree();   // show it from page load, before any device is connected

// Connect and Disconnect are mutually exclusive — exactly one is visible.
// Launch app only exists while connected (checkApk hides it again if the
// Capture app turns out not to be installed).
function setConnectedUi(connected: boolean) {
  btnConnect.style.display = connected ? "none" : "";
  btnDisconnect.style.display = connected ? "" : "none";
  btnDisconnect.disabled = !connected;
  btnLaunch.style.display = connected ? "" : "none";
  btnLaunch.disabled = !connected;
}

// ── EGO-132 Phase A: headset card + captures board ──────────────────────────
const hsDot = $("hsDot");
const hsText = $("hsText");
const hsSub = $("hsSub");
const btnSyncAll = $("btnSyncAll") as HTMLButtonElement;
const colNot = $("colNot");
const colBoth = $("colBoth");
const colGone = $("colGone");
// Capturer-facing summary (admins keep the three-bucket board).
const capsSimple = $("capsSimple");
const capsBoard = $("capsBoard");
const capsHeadline = $("capsHeadline");
const capsTotal = $("capsTotal");
const btnSyncSimple = $("btnSyncSimple") as HTMLButtonElement;
const btnClearSynced = $("btnClearSynced") as HTMLButtonElement;
const isAdmin = () => !!getProfile()?.is_admin;

function setHs(state: "ok" | "off" | "err", text: string, sub = "") {
  hsDot.className = "hs-dot" + (state === "ok" ? " ok" : state === "err" ? " err" : "");
  hsText.textContent = text;
  hsSub.textContent = sub;
}

// Presence without USB: the device row's last_seen_at (stamped while the
// headset's menu page polls the worker) says whether it's up somewhere.
async function showOfflinePresence() {
  setHs("off", "Not plugged in", "Connect the USB cable to sync.");
  try {
    const { data } = await supabase.from("devices")
      .select("label,last_seen_at").is("revoked_at", null)
      .order("last_seen_at", { ascending: false }).limit(1);
    const d = data?.[0];
    if (d?.last_seen_at) {
      const min = Math.round((Date.now() - new Date(d.last_seen_at).getTime()) / 60000);
      const ago = min < 1 ? "just now" : min < 60 ? `${min} min ago` : `${Math.round(min / 60)} h ago`;
      setHs("off", "Not plugged in", `${d.label || "Headset"} was online ${ago} — plug it in via USB to sync.`);
    }
  } catch { /* presence is best-effort */ }
}

const capRow = (id: string, extra = "", ghost = false) =>
  `<div class="cap${ghost ? " ghosted" : ""}"><span class="cid" title="${id}">${id}</span>${extra}</div>`;
// An uploaded capture is clickable → the verification view.
const capRowVerify = (id: string, ghost = false) =>
  `<div class="cap clickable${ghost ? " ghosted" : ""}" data-verify="${id}" title="Verify ${id}">` +
  `<span class="cid">${id}</span><span class="cverify">Verify →</span></div>`;

// Open the read-only verification view over the home; restore on close —
// including the scroll position, so closing from a deep uploads roster lands
// back on the row you came from.
function verifySession(id: string) {
  const scrollY = window.scrollY;
  $("appMain").style.display = "none";
  window.scrollTo(0, 0);
  void openVerify(id, { onClose: () => {
    $("appMain").style.display = "";
    window.scrollTo(0, scrollY);
    // Reviewed state may have changed in the editor — re-read the ledger so
    // the roster chips/counts are current.
    void uploadedStatus().then((u) => renderBoard(u, !!adb));
  } });
}

// Capturer view: no per-session list — just how many are waiting on the
// headset, how many they've uploaded in total, and the two contextual actions
// (Sync all; Clear all synced once everything on the headset is in the cloud).
function renderSimpleBoard(uploaded: Map<string, UploadedRow>, connected: boolean) {
  const remoteAll = [
    ...remoteCollections.map((c) => ({ ...c, kind: "collection" as const })),
    ...remoteSessions.map((s) => ({ ...s, kind: "session" as const })),
  ];
  const totalDone = [...uploaded.values()].filter((r) => r.status === "complete").length;
  capsTotal.textContent = totalDone
    ? `${totalDone} capture${totalDone === 1 ? "" : "s"} uploaded in total from your account.`
    : "Nothing uploaded yet — your finished captures will be counted here.";

  const notSynced = remoteAll.filter((e) => uploaded.get(e.id)?.status !== "complete");
  if (!connected) {
    capsHeadline.textContent = "Plug in the headset to sync your captures.";
    btnSyncSimple.style.display = "none";
    btnClearSynced.style.display = "none";
    return;
  }
  capsHeadline.textContent = !remoteAll.length
    ? "Nothing on the headset — go capture!"
    : notSynced.length
      ? `${notSynced.length} capture${notSynced.length === 1 ? "" : "s"} on the headset waiting to sync.`
      : `All ${remoteAll.length} capture${remoteAll.length === 1 ? "" : "s"} on the headset are synced.`;
  // Sync all: only when there's actually something it can upload.
  const syncable = notSynced.some((e) => e.kind === "session");
  btnSyncSimple.style.display = syncable ? "" : "none";
  btnSyncSimple.disabled = !syncable;
  // Clear: only when the headset holds captures and EVERY one is safely uploaded.
  btnClearSynced.style.display = remoteAll.length && !notSynced.length ? "" : "none";
  btnClearSynced.disabled = false;
}

// The three-bucket captures board: headset listing ⋈ upload ledger. With no
// device connected only the ledger side is knowable — the headset columns say
// so instead of guessing.
// The cleared column is a summary, not a roster: when the last capture was
// uploaded plus the account's lifetime total (the ledger is RLS-scoped to the
// signed-in user, so "all rows" ≡ "this account").
function goneSummary(uploaded: Map<string, UploadedRow>): string {
  const done = [...uploaded.values()].filter((r) => r.status === "complete");
  if (!done.length) return `<span class="mutedSm">Nothing uploaded yet.</span>`;
  const last = done.map((r) => r.uploadedAt).filter((t): t is string => !!t).sort().at(-1);
  const when = last
    ? new Date(last).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
    : "unknown";
  return `<div style="font-size:13px; font-weight:600; margin:2px 0 4px;">Last upload: ${when}</div>` +
    `<div class="mutedSm">${done.length} capture${done.length === 1 ? "" : "s"} uploaded from this account.</div>`;
}

function renderBoard(uploaded: Map<string, UploadedRow>, connected: boolean) {
  // Everyone gets the summary card (Sync all / Clear live there, so the
  // headset card's Sync now stays hidden); admins get the full board too.
  const admin = isAdmin();
  capsSimple.style.display = "";
  capsBoard.style.display = admin ? "" : "none";
  btnSyncAll.style.display = "none";
  renderSimpleBoard(uploaded, connected);
  void renderUploads(uploaded, verifySession);   // review doorway — every uploaded capture
  if (!admin) return;

  const remoteAll = [
    ...remoteCollections.map((c) => ({ ...c, kind: "collection" as const })),
    ...remoteSessions.map((s) => ({ ...s, kind: "session" as const })),
  ];
  if (!connected) {
    colNot.innerHTML = `<span class="mutedSm">Plug in the headset to see what's on it.</span>`;
    colBoth.innerHTML = `<span class="mutedSm">—</span>`;
    colGone.innerHTML = goneSummary(uploaded);
    btnSyncAll.disabled = true;
    return;
  }

  const notSynced = remoteAll.filter((e) => uploaded.get(e.id)?.status !== "complete");
  colNot.innerHTML = notSynced.length
    ? notSynced.map((e) => capRow(e.id,
        e.kind === "session"
          ? `<span class="csz">${fmtSize(e.bytes)}</span><button class="secondary" data-sync="${e.id}">Sync</button>`
          : `<span class="csz">collection · pull via Advanced</span>`)).join("")
    : `<span class="mutedSm">Everything on the headset is synced.</span>`;

  const both = remoteAll.filter((e) => uploaded.get(e.id)?.status === "complete");
  colBoth.innerHTML = both.length
    ? both.map((e) => capRowVerify(e.id)).join("")
    : `<span class="mutedSm">—</span>`;

  colGone.innerHTML = goneSummary(uploaded);

  for (const b of colNot.querySelectorAll<HTMLButtonElement>("[data-sync]")) {
    b.addEventListener("click", () => uploadFromHeadset(b.dataset.sync!, b));
  }
  wireVerify();
  btnSyncAll.disabled = !notSynced.some((e) => e.kind === "session");
}

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && isVerifyOpen()) closeVerify();
});

// Wire every uploaded-capture row in the board to open the verification view.
function wireVerify() {
  for (const el of document.querySelectorAll<HTMLElement>("#colBoth [data-verify]")) {
    el.addEventListener("click", () => verifySession(el.dataset.verify!));
  }
}

const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
const credentialStore = new AdbWebCredentialStore();

let adb: Adb | undefined;
let connectP: Promise<Adb> | null = null;   // serialize connects — two dispatchers on one USB endpoint corrupt the packet stream
let outRoot: FileSystemDirectoryHandle | undefined;
let remoteSessions: { id: string; files: number; bytes: number }[] = [];
let remoteCollections: { id: string; files: number; bytes: number }[] = [];

optDelete.checked = localStorage.getItem("ingest-delete-remote") === "1";
optDelete.addEventListener("change", () =>
  localStorage.setItem("ingest-delete-remote", optDelete.checked ? "1" : "0"));

async function connect(device: any): Promise<Adb> {
  if (connectP) return connectP;
  connectP = (async () => {
    if (adb) await disconnect();   // never run two transports over one endpoint
    setHs("off", "Connecting…", device.serial);
    const connection = await device.connect();
    const transport = await AdbDaemonTransport.authenticate({
      serial: device.serial, connection, credentialStore,
    });
    adb = new Adb(transport);
    // Surface a mid-session transport death (unplug, another adb owner, protocol
    // desync) instead of leaving the UI "connected" with every op rejecting.
    (transport as any).disconnected?.then?.(
      () => { if (adb) markDisconnected("transport closed"); },
      (err: any) => { if (adb) markDisconnected(err?.message ?? String(err)); },
    );
    setConnectedUi(true);
    btnRefresh.disabled = false;
    setHs("ok", "Plugged in — ready to sync", `${device.serial}`);
    log(`connected: ${device.serial}`);
    return adb;
  })();
  try { return await connectP; } finally { connectP = null; }
}

// A dead transport rejects every subsequent write with closed/closing-stream
// errors; a protocol desync surfaces as "Unknown command". Both mean the
// connection is gone — reset so the next Refresh/Connect re-establishes it.
function isTransportDead(err: any): boolean {
  return /CLOSED writable|closing writable|Unknown command|disconnected|NetworkError|transfer(In|Out)/i
    .test(String(err?.message ?? err));
}
function markDisconnected(reason: string) {
  adb = undefined;
  $("apkRow").style.display = "none";
  setConnectedUi(false);
  btnPullAll.disabled = true;
  btnRefresh.disabled = false;   // Refresh auto-reconnects via the persisted grant
  setHs("err", "Connection lost", "Reconnect the cable, then click Sync now or Refresh.");
  btnSyncAll.disabled = true;
  simpleBoardOffline("Connection lost — reconnect the cable to sync.");
  setCounts("");
  log(`connection lost: ${reason}`);
}

// The headset-side facts are gone; the simple card must not keep offering
// device actions. (The uploaded-total line stays — it's ledger truth.)
function simpleBoardOffline(headline: string) {
  capsHeadline.textContent = headline;
  btnSyncSimple.style.display = "none";
  btnClearSynced.style.display = "none";
}

async function autoConnect(): Promise<Adb | null> {
  if (!manager) { setHs("err", "WebUSB unavailable", "This browser can't talk to the headset — use desktop Chrome."); return null; }
  if (adb) return adb;
  const devices = await manager.getDevices();
  if (!devices.length) return null;
  try {
    return await connect(devices[0]);
  } catch (err: any) {
    setHs("err", "Connection failed", "See the Advanced log for details.");
    log(`connect failed: ${err?.message ?? err}` +
        (String(err).includes("claim") ? "  (another adb owner? quit MQDH / adb kill-server)" : ""));
    return null;
  }
}

async function disconnect() {
  try { await adb?.close(); } catch { /* already gone */ }
  adb = undefined;
  $("apkRow").style.display = "none";
  setConnectedUi(false);
  btnRefresh.disabled = true;
  btnPullAll.disabled = true;
  btnSyncAll.disabled = true;
  simpleBoardOffline("Plug in the headset to sync your captures.");
  void showOfflinePresence();
  setCounts("");
  log("disconnected — native adb can reclaim the device");
}

const PULL_LABEL: Record<PullProgress["phase"], string> = {
  walk: "Scanning session", pull: "Pulling from headset", verify: "Verifying every byte",
  delete: "Clearing headset copy", done: "Pulled",
};
function onProgress(p: PullProgress) {
  if (p.phase === "pull") {
    paint(PULL_LABEL.pull,
      p.file ? `${p.file} — ${p.filesDone + 1}/${p.filesTotal} · ${fmtMB(p.bytesDone)} / ${fmtMB(p.bytesTotal)}` : "starting…",
      p.bytesTotal ? p.bytesDone / p.bytesTotal : null);
  } else {
    paint(PULL_LABEL[p.phase] ?? p.phase, "", p.phase === "done" ? 1 : null);
  }
}

async function refresh() {
  if (!adb && !(await autoConnect())) return;
  try {
    remoteSessions = await listRemote(adb!, recRoot(PKG));
    remoteCollections = await listRemote(adb!, colRoot(PKG));
  } catch (err: any) {
    log(`list failed: ${err?.message ?? err}`);
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
    else setHs("err", "Couldn't list sessions", "See the Advanced log for details.");
    return;
  }
  setCounts(`${remoteSessions.length} sessions · ${remoteCollections.length} collections`);
  await renderRows();
  checkApk();   // fire-and-forget — APK staleness never blocks the session list
}

// ── Capture APK install/update over WebADB (EGO-96) ─────────────────────────
const apkRow = $("apkRow");
const apkStatus = $("apkStatus");
const btnApk = $("btnApk") as HTMLButtonElement;
const btnUninstall = $("btnUninstall") as HTMLButtonElement;
let latestRel: ReleaseInfo | null = null;
let installedNow: number | null = null;

async function checkApk() {
  if (!adb) { apkRow.style.display = "none"; return; }
  apkRow.style.display = "";
  apkStatus.textContent = "Capture app: checking…";
  btnApk.style.display = "none";
  btnUninstall.style.display = "none";
  try {
    const [rel, inst] = await Promise.all([latestRelease(), installedBuild(adb, PKG)]);
    latestRel = rel;
    installedNow = inst;
    btnUninstall.style.display = inst != null ? "" : "none";
    // Launch app is only offered when the Capture app is actually on the device.
    btnLaunch.style.display = inst != null ? "" : "none";
    if (!rel) { apkStatus.textContent = "Capture app: no releases published yet"; return; }
    if (inst != null && inst >= rel.build) {
      apkStatus.textContent = `Capture app: build ${inst} — up to date`;
      return;
    }
    apkStatus.textContent = inst == null
      ? `Capture app: not installed — latest is v${rel.version} (build ${rel.build}, ${fmtMB(rel.size)})`
      : `Capture app: build ${inst} installed — v${rel.version} (build ${rel.build}) available`;
    btnApk.textContent = inst == null ? "Install" : "Update";
    btnApk.style.display = "";
  } catch (err: any) {
    apkStatus.textContent = `Capture app: ${err?.message ?? err}`;
  }
}

btnApk.onclick = async () => {
  if (!adb || !latestRel) return;
  btnApk.disabled = true;
  const rel = latestRel;
  log(`downloading Capture v${rel.version} (build ${rel.build}, ${fmtMB(rel.size)})…`);
  try {
    const apk = await downloadApk(rel, (done, total) => {
      paint("Downloading Capture app", `${rel.key} — ${fmtMB(done)} / ${fmtMB(total)}`, total ? done / total : null);
    });
    log(`  sha256 verified ✓ — installing…`);
    await installApk(adb, apk, (phase) => paint("Installing Capture app", phase, null));
    hideProgress();
    log(`  installed build ${rel.build} ✓`);
    await checkApk();
  } catch (err: any) {
    hideProgress();
    log(`  APK install ERROR: ${err?.message ?? err}`);
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
  } finally {
    btnApk.disabled = false;
  }
};

// ── Uninstall with post-uninstall verification ───────────────────────────────
// Two steps in one overlay: an explicit confirm (recordings on the headset die
// with the app's data dir), then a verification page that re-queries the device
// instead of trusting `pm uninstall`'s exit line.
const uninstallModal = $("uninstallModal");
const uninstallAck = $("uninstallAck") as HTMLInputElement;
const btnUninstallGo = $("btnUninstallGo") as HTMLButtonElement;
const btnUninstallClose = $("btnUninstallClose") as HTMLButtonElement;
const uninstallChecks = $("uninstallChecks");

function closeUninstallModal() { uninstallModal.style.display = "none"; }

btnUninstall.onclick = () => {
  if (!adb) return;
  const stranded = remoteSessions.length + remoteCollections.length;
  $("uninstallInfo").textContent =
    `${PKG}${installedNow != null ? ` (build ${installedNow})` : ""} on ${adb.serial} — ` +
    (stranded ? `${stranded} session${stranded === 1 ? "" : "s"} still on the headset.`
              : "no sessions left on the headset.");
  uninstallAck.checked = false;
  btnUninstallGo.disabled = true;
  $("uninstallConfirm").style.display = "";
  $("uninstallResult").style.display = "none";
  uninstallModal.style.display = "flex";
};
uninstallAck.onchange = () => { btnUninstallGo.disabled = !uninstallAck.checked; };
$("btnUninstallCancel").onclick = closeUninstallModal;
btnUninstallClose.onclick = closeUninstallModal;

function addCheck(label: string): (ok: boolean, detail?: string) => void {
  const li = document.createElement("li");
  li.style.margin = "4px 0";
  li.textContent = `… ${label}`;
  uninstallChecks.appendChild(li);
  return (ok, detail) => {
    li.textContent = `${ok ? "✓" : "✗"} ${label}${detail ? ` — ${detail}` : ""}`;
    li.style.color = ok ? "var(--ok)" : "var(--err)";
  };
}

btnUninstallGo.onclick = async () => {
  if (!adb) return closeUninstallModal();
  $("uninstallConfirm").style.display = "none";
  $("uninstallResult").style.display = "";
  uninstallChecks.innerHTML = "";
  btnUninstallClose.disabled = true;
  log(`uninstalling ${PKG}…`);
  try {
    const ran = addCheck("pm uninstall");
    const out = String(await adb.subprocess.spawnAndWaitLegacy(`pm uninstall ${PKG}`)).trim();
    const ok = /Success/i.test(out);
    ran(ok, ok ? undefined : out.split("\n").pop());
    if (ok) {
      // Verify against the device, not pm's word: the package must be gone and
      // its scoped-storage data dir removed with it.
      const pkgGone = addCheck("package no longer installed");
      pkgGone((await installedBuild(adb, PKG)) == null);
      const dataGone = addCheck("app data & recordings dir removed");
      const ls = String(await adb.subprocess.spawnAndWaitLegacy(
        `ls -d /sdcard/Android/data/${PKG} 2>&1 || true`)).trim();
      dataGone(/No such file/i.test(ls), /No such file/i.test(ls) ? undefined : ls);
      log(`  uninstalled ${PKG} ✓`);
    } else {
      log(`  uninstall failed: ${out}`);
    }
    await checkApk();
    await refresh();
  } catch (err: any) {
    addCheck("uninstall")(false, err?.message ?? String(err));
    log(`  uninstall ERROR: ${err?.message ?? err}`);
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
  } finally {
    btnUninstallClose.disabled = false;
  }
};

async function renderRows() {
  type Row = { id: string; kind: "session" | "collection"; files?: number; bytes?: number; remote: boolean; local: boolean };
  const rowsById = new Map<string, Row>();
  for (const c of remoteCollections) rowsById.set(c.id, { ...c, kind: "collection", remote: true, local: false });
  for (const s of remoteSessions) rowsById.set(s.id, { ...s, kind: "session", remote: true, local: false });

  // Local pulled sessions join the table too (EGO-98) — after a verify-then-
  // delete pull they exist only here, and this is where Upload lives.
  if (outRoot) {
    const writer = handleWriter(outRoot);
    for (const r of rowsById.values()) r.local = await writer.exists(r.id);
    for await (const [name, handle] of (outRoot as any).entries()) {
      if (handle.kind === "directory" && SESSION_ID_RE.test(name) && !rowsById.has(name))
        rowsById.set(name, { id: name, kind: "session", remote: false, local: true });
    }
  }

  // Ledger state for the Upload column (own rows only via RLS). The board
  // renders even for an empty headset — "nothing here" is a state it shows.
  const uploaded = await uploadedStatus();
  renderBoard(uploaded, !!adb);

  const entries = [...rowsById.values()].sort((a, b) => a.id.localeCompare(b.id));
  if (!entries.length) {
    rowsEl.innerHTML = `<tr><td colspan="4" style="color:var(--muted)">Nothing on the headset${outRoot ? " or in the local folder" : ""}.</td></tr>`;
    btnPullAll.disabled = true;
    return;
  }

  const html: string[] = [];
  let anyMissing = false;
  for (const e of entries) {
    const status = uploaded.get(e.id)?.status;
    // Already-uploaded sessions don't need pulling again — no Pull button,
    // and they don't count toward "Pull all missing".
    const canPull = e.remote && !e.local && status !== "complete";
    if (canPull) anyMissing = true;
    const canUpload = e.local && e.kind === "session" && status !== "complete";
    // On-headset sessions get a direct Upload too: stage locally (picked
    // folder or OPFS), upload, delete the headset copy only after commit.
    const canUploadRemote = canPull && e.kind === "session";
    const action =
      (canPull ? `<button class="secondary" data-pull="${e.id}" data-kind="${e.kind}">Pull</button> ` : "") +
      (canUpload ? `<button class="secondary" data-upload="${e.id}">Upload</button>` :
       canUploadRemote ? `<button class="secondary" data-upload-remote="${e.id}">Upload</button>` : "");
    html.push(`<tr class="${e.local && !e.remote ? "local" : ""}">
      <td>${e.id}${e.kind === "collection" ? '<span class="tag">collection</span>' : ""}${e.local ? '<span class="tag">local</span>' : ""}${status === "complete" ? '<span class="tag">uploaded ✓</span>' : status ? `<span class="tag">${status}</span>` : ""}</td>
      <td class="num">${e.files ?? "—"}</td>
      <td class="num">${e.bytes != null ? fmtMB(e.bytes) : "—"}</td>
      <td>${action}</td>
    </tr>`);
  }
  rowsEl.innerHTML = html.join("");
  btnPullAll.disabled = !outRoot || !anyMissing;
  for (const b of rowsEl.querySelectorAll<HTMLButtonElement>("[data-pull]")) {
    b.addEventListener("click", () => pullOne(b.dataset.pull!, b.dataset.kind as "session" | "collection"));
  }
  for (const b of rowsEl.querySelectorAll<HTMLButtonElement>("[data-upload]")) {
    b.addEventListener("click", () => uploadOne(b.dataset.upload!, b));
  }
  for (const b of rowsEl.querySelectorAll<HTMLButtonElement>("[data-upload-remote]")) {
    b.addEventListener("click", () => uploadFromHeadset(b.dataset.uploadRemote!, b));
  }
  void updateDiskFree();   // pulls change it; cheap to refresh alongside the rows
}

const UPLOAD_LABEL: Record<UploadProgress["phase"], string> = {
  hash: "Checksumming", upload: "Uploading to cloud", commit: "Finalizing", done: "Uploaded",
};
// Last progress snapshot — attached to upload error reports so every event
// says how far it got (phase + file + MB done/total), even when the thrown
// error itself is vague.
let lastProgress: UploadProgress | null = null;
function progressMeta() {
  return lastProgress ? {
    progressPhase: lastProgress.phase, progressFile: lastProgress.file ?? null,
    mbDone: +(lastProgress.bytesDone / 1048576).toFixed(1),
    mbTotal: +(lastProgress.bytesTotal / 1048576).toFixed(1),
  } : {};
}
function onUploadProgress(p: UploadProgress) {
  lastProgress = p;
  if (p.phase === "upload") {
    paint(UPLOAD_LABEL.upload, `${p.file} — ${fmtMB(p.bytesDone)} / ${fmtMB(p.bytesTotal)}`,
      p.bytesTotal ? p.bytesDone / p.bytesTotal : null);
  } else {
    paint(UPLOAD_LABEL[p.phase] ?? p.phase, p.phase === "hash" ? (p.file ?? "") : "", p.phase === "done" ? 1 : null);
  }
}

async function uploadOne(id: string, btn: HTMLButtonElement) {
  if (!outRoot) return log("pick a local sessions folder first");
  btn.disabled = true;
  lastProgress = null;
  log(`uploading ${id}…`);
  try {
    const r = await uploadSession(outRoot, id, { log, onProgress: onUploadProgress });
    log(`  ${id}: ${r.files} files, ${fmtMB(r.bytes)} uploaded — manifest committed ✓` +
        (r.qaStatus ? ` (qa: ${r.qaStatus})` : ""));
    void renderTasks();    // done-counts moved
    await renderRows();
    setTimeout(hideProgress, 1500);
  } catch (err: any) {
    hideProgress();
    log(`  UPLOAD ERROR ${id}: ${err?.message ?? err}`);
    reportError(err, "upload", { sessionId: id, source: "local-folder", ...progressMeta() });
    btn.disabled = false;
  }
}

// Upload a session straight off the headset: stage it locally first (the
// picked folder, or OPFS when none is picked — no folder dialog needed), then
// upload from the staging copy. The headset copy is only removed AFTER the
// manifest commits (and only when delete-remote is checked) — safer than
// pull-with-delete followed by upload, where a failed upload would leave the
// local disk as the sole copy.
async function uploadFromHeadset(id: string, btn?: HTMLButtonElement) {
  if (!adb) return log("not connected");
  if (btn) btn.disabled = true;
  const usingOpfs = !outRoot;
  const staging = outRoot ?? await navigator.storage.getDirectory();
  const writer = handleWriter(staging);
  const dropStaging = async () => {
    if (usingOpfs) { try { await staging.removeEntry(id, { recursive: true }); } catch { /* nothing staged */ } }
  };
  // Which leg of pull→transcribe→proxy→upload died — the error alone doesn't
  // always say (e.g. a bare DOMException from an OPFS write during staging).
  let stage = "stage-pull";
  let sessionBytes: number | null = null;
  lastProgress = null;
  try {
    if (!(await writer.exists(id))) {
      const known = remoteSessions.find((e) => e.id === id);
      sessionBytes = known?.bytes ?? null;
      if (known && !(await confirmSpace(known.bytes, `session ${id}`))) {
        log(`  upload of ${id} cancelled (low disk space for staging)`);
        if (btn) btn.disabled = false;
        return;
      }
      log(`pulling ${id} for upload (headset copy kept until upload commits)…`);
      const r = await pullSession(adb, PKG, id, writer, { deleteRemote: false, onProgress });
      if (!r.verified) throw new Error(`pull verify mismatch: ${r.mismatches.join(", ")}`);
      log(`  pulled ${r.files} files, ${fmtMB(r.bytes)} — verified ✓`);
      sessionBytes = r.bytes;
    }
    // PII mask burn-in (EGO-163): if the wearer marked privacy regions in the
    // headset, black them out of left/right.mp4 BEFORE anything else touches the
    // video — the review proxy below must be cut from the MASKED eyes. Unlike
    // transcribe/proxy this is deliberately NOT swallowed: a masked session that
    // cannot be masked aborts its sync rather than uploading raw footage.
    // (upload.ts's preflight refuses it too, belt and braces.)
    stage = "mask-burn";
    {
      const sdir = await staging.getDirectoryHandle(id);
      paint("Masking private regions", "preparing video…", null);
      try {
        const mb = await maskBurnInto(sdir, { log, onProgress: (frac) =>
          paint("Masking private regions", `${Math.round(frac * 100)}%`, frac) });
        if (mb.skipped && mb.reason !== "no masks") log(`  mask burn-in: ${mb.reason}`);
      } catch (e: any) {
        throw new Error(
          `PII mask burn-in failed — ${id} will NOT be uploaded (its private regions are still visible): ${e?.message ?? e}`,
          { cause: e },
        );
      }
    }
    stage = "upload";
    // Transcribe at ingest (EGO-132 B): STT runs on the staged session so
    // transcript.json rides the upload as derived/transcript.json. Non-fatal —
    // a failed/absent transcript never blocks the capture from uploading.
    try {
      const sdir = await staging.getDirectoryHandle(id);
      paint("Transcribing narration", "preparing audio…", null);
      await transcribeInto(sdir, { log, onProgress: (i, n) =>
        paint("Transcribing narration", `window ${i + 1} of ${n}…`, null) });
    } catch (e: any) {
      log(`  transcription skipped: ${e?.message ?? e}`);
      reportError(e, "transcribe-skip", { sessionId: id });   // non-fatal, but visible
    }
    // Review proxy at ingest (EGO-136 B): transcode left.mp4 → proxy.mp4 on the
    // staged session so it rides the upload as derived/proxy.mp4 (upload.ts's
    // DERIVED_RE routes proxy.mp4). Non-fatal — a failed/absent proxy never blocks
    // the capture from uploading.
    try {
      const sdir = await staging.getDirectoryHandle(id);
      paint("Generating review proxy", "preparing video…", null);
      await proxyInto(sdir, { log, onProgress: (frac) =>
        paint("Generating review proxy", `${Math.round(frac * 100)}%`, frac) });
    } catch (e: any) {
      log(`  review proxy skipped: ${e?.message ?? e}`);
      reportError(e, "proxy-skip", { sessionId: id });   // non-fatal, but visible
    }
    const u = await uploadSession(staging, id, { log, onProgress: onUploadProgress });
    log(`  ${id}: ${u.files} files, ${fmtMB(u.bytes)} uploaded — manifest committed ✓` +
        (u.qaStatus ? ` (qa: ${u.qaStatus})` : ""));
    if (optDelete.checked && adb) {
      const gone = await rmRemote(adb, `${recRoot(PKG)}/${id}`);
      log(gone ? `  headset copy removed` : `  headset copy NOT removed — check the device`);
    }
    await dropStaging();   // committed in R2 — an invisible OPFS copy is just residue
    void renderTasks();    // done-counts moved
    await refresh();
    if (syncCtx.total <= 1) setTimeout(hideProgress, 1500);   // batch loop hides at its end
  } catch (err: any) {
    hideProgress();
    log(`  UPLOAD ERROR ${id}: ${err?.message ?? err}`);
    reportError(err, "upload", {
      sessionId: id, source: "headset", stage, stagedToOpfs: usingOpfs,
      sessionBytes, transportDead: isTransportDead(err),
      errName: err?.name ?? null,   // DOMException name (QuotaExceededError etc.)
      ...progressMeta(),
    });
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
    await dropStaging();   // headset copy was never deleted pre-commit, so staging is redundant
    if (btn) btn.disabled = false;
  }
}

async function pullOne(id: string, kind: "session" | "collection") {
  if (!adb) return log("not connected");
  if (!outRoot) return log("pick a local sessions folder first");
  // Space preflight — for collections the listed bytes exclude the task clips
  // pulled alongside, so this is a floor, not the full footprint.
  const known = (kind === "collection" ? remoteCollections : remoteSessions).find((e) => e.id === id);
  if (known && !(await confirmSpace(known.bytes, `${kind} ${id}`))) {
    return log(`  pull of ${id} cancelled (low disk space)`);
  }
  const writer = handleWriter(outRoot);
  const del = optDelete.checked;
  log(`pulling ${kind} ${id}${del ? " (delete-after-verify)" : " (keep remote)"}…`);
  try {
    const fn = kind === "collection" ? pullCollection : pullSession;
    const r = await fn(adb, PKG, id, writer, { deleteRemote: del, onProgress });
    const mbps = r.seconds > 0 ? (r.bytes / 1048576 / r.seconds).toFixed(1) : "?";
    log(`  ${r.id}: ${r.files} files, ${fmtMB(r.bytes)} in ${r.seconds.toFixed(1)}s (${mbps} MiB/s) — ` +
        (r.verified ? "verified ✓" : `MISMATCH: ${r.mismatches.join(", ")}`) +
        (r.deletedRemote ? ", remote removed" : ""));
    await refresh();
  } catch (err: any) {
    log(`  ERROR pulling ${id}: ${err?.message ?? err}`);
    reportError(err, "pull", { sessionId: id, kind, errName: err?.name ?? null,
      transportDead: isTransportDead(err) });
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
  }
}

async function pullAllMissing() {
  if (!adb || !outRoot) return;
  const writer = handleWriter(outRoot);
  const uploaded = await uploadedStatus();   // skip already-uploaded, matching the hidden Pull buttons
  for (const c of remoteCollections)
    if (uploaded.get(c.id)?.status !== "complete" && !(await writer.exists(c.id))) await pullOne(c.id, "collection");
  for (const s of remoteSessions)
    if (uploaded.get(s.id)?.status !== "complete" && !(await writer.exists(s.id))) await pullOne(s.id, "session");
}

// Interactive connect (WebUSB chooser) — used by the button AND the wizard.
async function connectInteractive(): Promise<Adb | null> {
  if (!manager) { setHs("err", "WebUSB unavailable", "This browser can't talk to the headset — use desktop Chrome."); return null; }
  if (adb) return adb;
  try {
    const existing = await autoConnect();   // reuse a standing grant silently
    if (existing) return existing;
    const device = await manager.requestDevice();
    if (!device) return null;
    return await connect(device);
  } catch (err: any) {
    setHs("err", "Connection failed", "See the Advanced log for details.");
    log(`connect failed: ${err?.message ?? err}` +
        (String(err).includes("claim") ? "  (another adb owner? quit MQDH / adb kill-server)" : ""));
    return null;
  }
}

btnConnect.onclick = async () => {
  if (await connectInteractive()) await refresh();
};
btnDisconnect.onclick = disconnect;
// monkey (not `am start`) — an explicit intent can open the app as a 2D panel;
// the LAUNCHER category via monkey starts it immersive.
btnLaunch.onclick = async () => {
  if (!adb) return log("not connected");
  btnLaunch.disabled = true;
  log("launching Capture on headset…");
  try {
    const out = String(await adb.subprocess.spawnAndWaitLegacy(
      `monkey -p ${PKG} -c android.intent.category.LAUNCHER 1`));
    if (/Events injected: 1/.test(out)) {
      log("  launch sent ✓ — put the headset on (an unworn Quest ignores launches)");
    } else {
      log(`  launch failed: ${out.trim().split("\n").pop()}`);
    }
  } catch (err: any) {
    log(`  launch failed: ${err?.message ?? err}`);
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
  } finally {
    btnLaunch.disabled = !adb;
  }
};
btnRefresh.onclick = refresh;
btnPullAll.onclick = pullAllMissing;
// Sync now — upload every on-headset session the ledger doesn't have yet,
// one at a time (the transport is a single USB endpoint; parallel pulls fight).
// Shared by the admin headset-card button and the capturer captures-card one.
async function syncAllNow() {
  if (!adb) return;
  btnSyncAll.disabled = true;
  btnSyncSimple.disabled = true;
  try {
    const uploaded = await uploadedStatus();
    const todo = remoteSessions.filter((s) => uploaded.get(s.id)?.status !== "complete");
    if (!todo.length) { log("Sync now: nothing to upload"); return; }
    log(`Sync now: ${todo.length} session${todo.length === 1 ? "" : "s"} to upload`);
    syncCtx = { idx: 0, total: todo.length };   // drives the "session k/n" progress label
    for (const [i, s] of todo.entries()) {
      if (!adb) break;   // transport died mid-run
      syncCtx.idx = i + 1;
      await uploadFromHeadset(s.id);
    }
  } finally {
    syncCtx = { idx: 0, total: 0 };
    hideProgress();
    btnSyncAll.disabled = !adb;
    btnSyncSimple.disabled = !adb;
  }
}
btnSyncAll.onclick = syncAllNow;
btnSyncSimple.onclick = syncAllNow;

// Clear all synced videos from the headset. Only reachable when the board
// showed everything as synced, but never trust a stale render: re-read the
// ledger and refuse to delete anything it doesn't mark complete.
btnClearSynced.onclick = async () => {
  if (!adb) return;
  btnClearSynced.disabled = true;
  try {
    const uploaded = await uploadedStatus();
    const all = [
      ...remoteCollections.map((c) => ({ id: c.id, root: colRoot(PKG) })),
      ...remoteSessions.map((s) => ({ id: s.id, root: recRoot(PKG) })),
    ];
    const unsynced = all.filter((e) => uploaded.get(e.id)?.status !== "complete");
    if (unsynced.length) {
      log(`Clear synced: ${unsynced.length} capture${unsynced.length === 1 ? "" : "s"} not fully uploaded yet — nothing deleted`);
      await refresh();
      return;
    }
    if (!all.length) return;
    if (!confirm(`Remove ${all.length} synced capture${all.length === 1 ? "" : "s"} from the headset?\n\nEvery one is safely uploaded — the cloud copies are untouched. This frees up space on the headset.`)) return;
    log(`clearing ${all.length} synced capture${all.length === 1 ? "" : "s"} from headset…`);
    for (const e of all) {
      if (!adb) break;   // transport died mid-run
      const gone = await rmRemote(adb, `${e.root}/${e.id}`);
      log(gone ? `  ${e.id} removed ✓` : `  ${e.id} NOT removed — check the device`);
    }
    await refresh();
  } catch (err: any) {
    log(`  CLEAR ERROR: ${err?.message ?? err}`);
    reportError(err, "clear-synced", { transportDead: isTransportDead(err) });
    if (isTransportDead(err)) markDisconnected(err?.message ?? String(err));
  } finally {
    btnClearSynced.disabled = false;
  }
};
btnFolder.onclick = async () => {
  try {
    outRoot = await (window as any).showDirectoryPicker({ mode: "readwrite" });
    $("outDirHint").style.display = "none";
    outDirEl.style.display = "";
    outDirEl.textContent = `Local sessions folder: ${outRoot!.name}`;
    log(`local folder: ${outRoot!.name}`);
    await renderRows();
  } catch { /* user cancelled */ }
};

// Boot: the auth gate (EGO-95) decides what starts — the full Portal app for
// admins/onboarded users, or the wizard (EGO-99) for fresh contractors.
initAuth({
  bootApp: async () => {
    void renderTasks();
    const a = await autoConnect();
    if (a) await refresh();
    else {
      void showOfflinePresence();
      renderBoard(await uploadedStatus(), false);
    }
  },
  startOnboarding: (step) => {
    startWizard({
      getAdb: () => adb,
      connect: connectInteractive,
      onDone: () => {
        // Drop #wizard first or the reload just re-enters the wizard.
        if (location.hash === "#wizard") history.replaceState(null, "", location.pathname);
        location.reload();   // simplest handoff into the full app
      },
    }, step).catch((e) => {
      log(`wizard error: ${e?.message ?? e}`);
      reportError(e, "wizard");
    });
  },
});

// Footer "Setup guide": re-enter the onboarding wizard from the full Portal.
// (NOT "btnSetup" — that id is the invite-landing Continue button in auth.ts.)
document.getElementById("btnSetupGuide")?.addEventListener("click", () => {
  location.hash = "#wizard";
  location.reload();
});

// ── E2E hooks (automation only) ─────────────────────────────────────────────
// Pulls the newest session into OPFS (no permission dialogs) and returns a
// JSON-able result. keepRemote defaults to true so automation is non-destructive.
(window as any).__e2e = {
  connect: autoConnect,
  list: async () => {
    if (!(await autoConnect())) throw new Error("no device");
    await refresh();
    return { sessions: remoteSessions, collections: remoteCollections };
  },
  pullNewestToOpfs: async (opts?: { deleteRemote?: boolean }) => {
    if (!(await autoConnect())) throw new Error("no device");
    await refresh();
    const newest = remoteSessions[remoteSessions.length - 1];
    if (!newest) throw new Error("no sessions on device");
    const opfs = await navigator.storage.getDirectory();
    const writer = handleWriter(opfs);
    return await pullSession(adb!, PKG, newest.id, writer, {
      deleteRemote: opts?.deleteRemote ?? false, onProgress,
    });
  },
  // EGO-163: run the PII burn on an OPFS-staged session. The dir picker and
  // WebUSB can't be driven headlessly, so this is how the browser leg of the
  // mask path gets exercised (see tools/ dev notes) — same code path Sync uses.
  maskBurnFromOpfs: async (id: string) => {
    const opfs = await navigator.storage.getDirectory();
    return await maskBurnInto(await opfs.getDirectoryHandle(id), { log });
  },
  qaFromOpfs: async (id: string) => {
    const opfs = await navigator.storage.getDirectory();
    const { runQa } = await import("./qa.ts");
    return await runQa(await opfs.getDirectoryHandle(id));
  },
  uploadFromOpfs: async (id: string, opts?: { isTest?: boolean }) => {
    const opfs = await navigator.storage.getDirectory();
    return await uploadSession(opfs, id, { log, isTest: opts?.isTest });
  },
  opfsClear: async () => {
    const opfs = await navigator.storage.getDirectory();
    for await (const [name] of (opfs as any).entries()) {
      await opfs.removeEntry(name, { recursive: true });
    }
    return "cleared";
  },
  openVerify: (id: string) => verifySession(id),   // drive the verification view in tests
  disconnect,
};
